import FavoriteIcon from '@material-ui/icons/Favorite';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import { useWeb3React } from '@web3-react/core';
import AnimationHover from 'components/AnimationHover';
import { StyledAnimatedCard } from 'components/Card/Card';
import Ribbon from 'components/Ribbon';
import StyledSkeleton from 'components/Skeleton';
import { SYSTEM_ADDRESS } from 'config';
import { MODULES } from 'config/constants/modules';
import { useHunnyMallContract } from 'hooks/contracts/useHunnyMallContract';
import { NFTItem } from 'models/NFT/nftItem.model';
import { State } from 'models/state.model';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useSpring } from 'react-spring';
import { NFTProductStatus, NFTStatusEnum } from 'shared/enums/NFT.enum';
import { useLikeNFT } from 'state/nft/hooks';
import styled from 'styled-components';
import NumberDisplay from '../NumberDisplay';

const NFTItemDisplay: React.FC<{ item: NFTItem; previousPage?: string }> = ({
	item,
	previousPage,
}) => {
	const { likedNftIds } = useSelector((state: State) => state.nft);
	const parsedOwner =
		item.productStatus === NFTProductStatus.BlockchainProductStatusCreated
			? item.owner
			: item.listedBy;
	const { account } = useWeb3React();

	const isMyFavorite = useMemo(
		() => likedNftIds.find((itemLike) => itemLike.id === item.id)?.isLike,
		[likedNftIds]
	);
	const { toggleLikeNFT, totalLike } = useLikeNFT(item);

	const [isSpringEnded, setIsSpringEnded] = useState(false);

	const isOnAuction = useMemo(
		() => item.status === NFTStatusEnum.OnAuction && item.marketListId >= 0,
		[item]
	);

	const styleOpen = useSpring({
		from: { scale: 0, opacity: 0 },
		to: { scale: 1, opacity: 1 },
		config: { tension: 200, friction: 22 },
		onResolve: () => {
			setIsSpringEnded(true);
		},
	});

	const history = useHistory();

	const gotoNFTDetails = useCallback(() => {
		history.push({
			pathname: `${MODULES.DETAIL_NFT}/${item.id}`,
			state: {
				from: previousPage || '',
			},
		});
	}, [item]);

	const onChangeLikeNFT = useCallback(() => {
		toggleLikeNFT(!isMyFavorite);
	}, [likedNftIds, isMyFavorite]);

	return (
		<StyledNavigateWrapper onClick={gotoNFTDetails}>
			<AnimationHover>
				<StyledContainer style={{ ...styleOpen }}>
					{item.status === NFTStatusEnum.OnAuction && (
						<Ribbon size="small">
							<Trans>On Auction</Trans>
						</Ribbon>
					)}

					<StyleImageContainer>
						<StyledNFTLoveContainer
							onClick={(e: any) => {
								e.stopPropagation();
								onChangeLikeNFT();
							}}
						>
							{isMyFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
							{totalLike}
						</StyledNFTLoveContainer>
						{}

						{isSpringEnded ? (
							<StyledImage background={item.smallMediaUrl} />
						) : (
							<StyledImageWrapper>
								<StyledImageLoader>
									<StyledSkeleton style={{ height: '100%' }} />
								</StyledImageLoader>
							</StyledImageWrapper>
						)}
					</StyleImageContainer>

					<StyledNFTName>{item.name}</StyledNFTName>
					{item.price && (
						<StyledValueContainer>
							<div className="label">
								{isOnAuction ? <Trans>Current bid</Trans> : <Trans>Price</Trans>}
							</div>
							<div className="value">
								<img src={item.currency.logo} style={{ width: 16, marginRight: 8 }} />

								<NumberDisplay value={item.price} fixed={8} decimals={0} />
							</div>
						</StyledValueContainer>
					)}

					<StyledValueContainer>
						<div className="label">
							<Trans>Owner</Trans>
						</div>
						<div className="value">
							<StyledWrapAccount>
								{account && parsedOwner?.toLowerCase() === account.toLowerCase() ? (
									<Trans>You</Trans>
								) : parsedOwner?.toLowerCase() === SYSTEM_ADDRESS ? (
									<StyledOwnerName>Hunny</StyledOwnerName>
								) : (
									`${parsedOwner.substring(0, 6)}...${parsedOwner.substring(
										parsedOwner.length - 4,
										parsedOwner.length
									)}`
								)}
							</StyledWrapAccount>
						</div>
					</StyledValueContainer>
				</StyledContainer>
			</AnimationHover>
		</StyledNavigateWrapper>
	);
};

const StyledOwnerName = styled.div``;
const StyledNavigateWrapper = styled.div``;

const StyledImage = styled.div<{ background: string }>`
	background-size: contain;
	background-image: url(${(props) => props.background});
	background-repeat: no-repeat;
	background-position: center;
	position: absolute;
	max-width: 100%;
	max-height: 100%;
	display: flex;
	top: 0;
	right: 0;
	left: 0;
	bottom: 0;
`;

const StyledImageWrapper = styled.div`
	position: absolute;
	max-width: 100%;
	max-height: 100%;
	display: flex;
	top: 0;
	right: 0;
	left: 0;
	bottom: 0;
`;

const StyledContainer = styled(StyledAnimatedCard)`
	height: 100%;
	position: relative;
	display: flex;
	flex-direction: column;
	box-sizing: border-box;
	justify-content: flex-end;
	cursor: pointer;
	border: none !important;

	padding: 16px;
	align-items: center;

	// Kelvin move to Component AnimationHover
	/* &:hover {
		border: 1px solid ${(props) => props.theme.color.purple[200]};
		box-shadow: 0 0 2px ${(props) => props.theme.color.purple[200]},
			0 0 4px ${(props) => props.theme.color.purple[200]},
			0 0 16px ${(props) => props.theme.color.purple[200]};
	} */
`;

const StyledNFTImage = styled.img`
	width: 100%;
	border-radius: 4px;
	object-fit: fill;
`;

const StyleImageContainer = styled.div`
	width: 100%;
	position: relative;
	padding-top: 140%;
`;

const StyledWrapAccount = styled.span`
	color: white;
	font-size: 14.5px;
	font-weight: normal;
`;

const StyledNFTLoveContainer = styled.div`
	position: absolute;
	top: 0px;
	right: 0px;
	background: #fff;
	z-index: 3;

	border-radius: 4px;
	padding: 4px 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: bold;
	font-size: 14px;
	line-height: 16px;
	color: ${(props) => props.theme.color.purple[200]};

	cursor: pointer;

	svg {
		width: 16px;
		margin-right: 6px;
	}
`;

const StyledNFTName = styled.div`
	font-style: normal;
	font-weight: bold;
	font-size: 16px;
	line-height: 18px;
	color: #fff;
	margin-bottom: 4px;
	margin-top: 22px;
	width: 100%;
`;

const StyledValueContainer = styled.div`
	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;
	margin-top: 8px;
	width: 100%;

	.label {
		font-style: normal;
		font-weight: normal;
		font-size: 14px;
		line-height: 16px;
		color: ${(props) => props.theme.color.grey[600]};
	}

	.value {
		font-weight: bold;
		font-size: 14px;
		line-height: 16px;
		color: #fff;
		display: flex;
		align-items: center;

		@media (max-width: 320px) {
			margin-top: 10px;
			width: 100%;
			white-space: nowrap;
			text-overflow: ellipsis;
			overflow: hidden;
		}
	}
`;

const StyledImageLoader = styled.div`
	position: absolute;
	width: 100%;
	height: 100%;
	top: 0px;
	left: 0px;
`;

export default NFTItemDisplay;
